import { useState, FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link as RouterLink } from 'react-router-dom';
import {
  PsiContractingIdentityDTO,
  PsiContractingIdentitySchema,
  PsiContractingProfileDTO,
} from '@inversor-hub/types';
import { useGetPsiContractingProfile, useUpdatePsiContractingProfile } from '@/lib/api/psi-profile';
import { getSignErrorMessage } from '@/features/user/contract-data';
import useSnackbar from '@/hooks/useSnackbar';

type Company = Extract<PsiContractingIdentityDTO, { type: 'COMPANY' }>;
const COMPANY_FIELDS: { name: Exclude<keyof Company, 'type'>; label: string; columns: number }[] = [
  { name: 'legalName', label: 'Razón social', columns: 8 },
  { name: 'taxId', label: 'NIF de la empresa', columns: 4 },
  { name: 'address', label: 'Domicilio social', columns: 12 },
  { name: 'postalCode', label: 'Código postal', columns: 4 },
  { name: 'city', label: 'Municipio', columns: 4 },
  { name: 'province', label: 'Provincia', columns: 4 },
  { name: 'representativeCapacity', label: 'Cargo del representante', columns: 12 },
];
const CONTRACTING_OPTIONS = [
  { value: 'PERSONAL', label: 'Persona física', description: 'Firmo en mi propio nombre.' },
  { value: 'COMPANY', label: 'Empresa', description: 'Firmo en representación de una sociedad.' },
] as const;

function ContractingForm({ profile }: { profile: PsiContractingProfileDTO }) {
  const [type, setType] = useState(profile.identity.type);
  const [company, setCompany] = useState<Company>(
    profile.identity.type === 'COMPANY'
      ? profile.identity
      : {
          type: 'COMPANY',
          legalName: '',
          taxId: '',
          address: '',
          postalCode: '',
          city: '',
          province: '',
          representativeCapacity: '',
        }
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const update = useUpdatePsiContractingProfile();
  const { show } = useSnackbar();
  const identity = type === 'PERSONAL' ? { type } : company;
  const hasChanges = JSON.stringify(identity) !== JSON.stringify(profile.identity);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = PsiContractingIdentitySchema.safeParse(identity);
    if (!parsed.success) {
      setErrors(
        Object.fromEntries(parsed.error.issues.map((issue) => [issue.path[0], issue.message]))
      );
      return;
    }
    setErrors({});
    update.mutate(parsed.data, {
      onSuccess: () => show({ title: 'Datos de contratación guardados', variant: 'success' }),
    });
  };

  return (
    <Stack component="form" noValidate onSubmit={submit} spacing={3}>
      <FormControl disabled={update.isPending}>
        <FormLabel
          id="contracting-type-label"
          sx={{ color: 'text.primary', typography: 'subtitle2', mb: 1.5 }}
        >
          ¿Cómo quieres firmar tus colaboraciones?
        </FormLabel>
        <RadioGroup
          aria-labelledby="contracting-type-label"
          name="contracting-type"
          value={type}
          onChange={(event) => {
            setType(event.target.value as typeof type);
            setErrors({});
          }}
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}
        >
          {CONTRACTING_OPTIONS.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio size="small" />}
              sx={{
                m: 0,
                p: 1.5,
                alignItems: 'flex-start',
                border: '1px solid',
                borderRadius: 2,
                borderColor: type === option.value ? 'primary.main' : 'divider',
                bgcolor: type === option.value ? 'primary.50' : 'background.paper',
                '& .MuiRadio-root': { p: 0, mr: 1.5, mt: 0.25 },
                '&:has(.Mui-focusVisible)': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 3,
                },
              }}
              label={
                <Box component="span" sx={{ display: 'block' }}>
                  <Typography component="span" variant="body2" fontWeight={600} display="block">
                    {option.label}
                  </Typography>
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    {option.description}
                  </Typography>
                </Box>
              }
            />
          ))}
        </RadioGroup>
      </FormControl>

      {type === 'COMPANY' && (
        <Box>
          <Typography component="h3" variant="subtitle2" mb={0.5}>
            Datos de la empresa
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2.5}>
            Aparecerán en el contrato de colaboración. Todos los campos son obligatorios.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 2 }}>
            {COMPANY_FIELDS.map(({ name, label, columns }) => (
              <TextField
                key={name}
                name={name}
                label={label}
                value={company[name]}
                required
                fullWidth
                disabled={update.isPending}
                sx={{ gridColumn: { xs: 'span 12', sm: `span ${columns}` } }}
                onChange={(event) =>
                  setCompany((previous) => ({ ...previous, [name]: event.target.value }))
                }
                error={Boolean(errors[name])}
                helperText={
                  errors[name] ||
                  (name === 'representativeCapacity'
                    ? 'Por ejemplo, administrador o apoderado.'
                    : undefined)
                }
                inputProps={{
                  maxLength: name === 'postalCode' ? 5 : 250,
                  inputMode: name === 'postalCode' ? 'numeric' : 'text',
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <PersonOutlineIcon sx={{ color: 'text.secondary', mt: 0.25 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary">
              {type === 'COMPANY' ? 'Representante y firmante' : 'Firmante'}
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
              {profile.representative.name || 'Nombre pendiente'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {[profile.representative.documentType, profile.representative.documentNumber]
                .filter(Boolean)
                .join(' ') || 'Documento pendiente'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Se utilizará la firma que tienes guardada en tu perfil.
            </Typography>
            <Link
              component={RouterLink}
              to="/perfil?tab=personal"
              underline="hover"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 1,
                typography: 'body2',
                fontWeight: 500,
              }}
            >
              Editar datos personales y firma <ArrowForwardIcon sx={{ fontSize: 16 }} />
            </Link>
          </Box>
        </Stack>
      </Box>

      {!hasChanges && profile.missingFields.length > 0 && (
        <Alert severity="warning">
          Para poder firmar, completa: {profile.missingFields.join(', ')}.{' '}
          <Link component={RouterLink} to="/perfil?tab=configuracion" color="inherit">
            Revisar datos bancarios
          </Link>
        </Alert>
      )}
      {update.isError && <Alert severity="error">{getSignErrorMessage(update.error)}</Alert>}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}
      >
        <Box role="status">
          {hasChanges ? (
            <Typography variant="body2" color="text.secondary">
              Tienes cambios sin guardar.
            </Typography>
          ) : profile.readyToContract ? (
            <Stack direction="row" spacing={0.75} alignItems="center">
              <CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'success.main' }} />
              <Typography variant="body2" color="text.secondary">
                Todo listo para colaborar.
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Completa los datos pendientes para firmar.
            </Typography>
          )}
        </Box>
        <Button
          type="submit"
          variant="contained"
          disableElevation
          disabled={update.isPending || !hasChanges}
          startIcon={update.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ flexShrink: 0, minHeight: 42, px: 3 }}
        >
          {update.isPending ? 'Guardando…' : 'Guardar cambios'}
        </Button>
      </Stack>
    </Stack>
  );
}

export default function PsiContractingSection() {
  const query = useGetPsiContractingProfile();
  if (query.isPending)
    return (
      <Stack spacing={2} role="status" aria-label="Cargando datos de contratación">
        <Typography variant="body2" color="text.secondary">
          Cargando datos de contratación…
        </Typography>
        <Skeleton variant="rounded" height={88} />
        <Skeleton variant="rounded" height={120} />
      </Stack>
    );
  if (query.isError)
    return (
      <Alert severity="error" action={<Button onClick={() => query.refetch()}>Reintentar</Button>}>
        No se pudieron cargar los datos de contratación.
      </Alert>
    );
  return <ContractingForm key={JSON.stringify(query.data.identity)} profile={query.data} />;
}
